/**
 * Cleanup utilities for all Location-based hierarchy tables
 * WARNING: These functions will delete ALL records for a client
 * 
 * Tables included in cleanup:
 * - Location
 * - LocationLaborTargets
 * - LocationEmployee (NEW)
 * - OrgUnit
 * - OrgUnitEmployee (NEW)
 * - Employee (NEW)
 * - HierarchyChangeLog
 * 
 * Tables removed from schema (no longer cleaned):
 * - ManagerAssignmentHistory (replaced by LocationEmployee/OrgUnitEmployee)
 * - LocationAssignmentHistory (functionality moved to HierarchyChangeLog)
 * - OrgUnitHistory (functionality moved to HierarchyChangeLog)
 */

import { API, graphqlOperation } from 'aws-amplify';
import { 
  locationByClientId,
  listLocationLaborTargets,
  // managerAssignmentsByClient, // REMOVED: ManagerAssignmentHistory removed from schema
  hierarchyChangesByClient,
  listOrgUnits,
  listEmployees,
  listLocationEmployees,
  listOrgUnitEmployees,
  listRoleDefinitions,
  listUsers
} from 'graphql/queries';
import { 
  deleteLocation,
  deleteLocationLaborTargets,
  // deleteManagerAssignmentHistory, // REMOVED: ManagerAssignmentHistory removed from schema
  deleteHierarchyChangeLog,
  deleteOrgUnit,
  deleteEmployee,
  deleteLocationEmployee,
  deleteOrgUnitEmployee,
  deleteRoleDefinition,
  updateUser
} from 'graphql/mutations';

/**
 * Delete all Location records for a client
 */
export async function cleanupLocations(clientId, options = {}) {
  const { dryRun = false, onProgress } = options;
  
  console.log(`🧹 Cleaning Location records for client: ${clientId}`);
  console.log(`Mode: ${dryRun ? 'DRY RUN' : 'LIVE'}`);
  
  const results = { totalFound: 0, deleted: 0, errors: [] };
  
  try {
    let nextToken = null;
    let allRecords = [];
    
    // Fetch all records
    do {
      const result = await API.graphql(
        graphqlOperation(locationByClientId, {
          clientId,
          limit: 100,
          nextToken
        })
      );
      
      const items = result.data.locationByClientId?.items || [];
      allRecords = allRecords.concat(items);
      nextToken = result.data.locationByClientId?.nextToken;
      
      if (onProgress) {
        onProgress({ phase: 'fetching', found: allRecords.length });
      }
    } while (nextToken);
    
    results.totalFound = allRecords.length;
    console.log(`Found ${results.totalFound} Location records`);
    
    if (dryRun) {
      console.log(`DRY RUN - Would delete ${results.totalFound} records`);
      return results;
    }
    
    // Delete records
    for (const record of allRecords) {
      try {
        await API.graphql(
          graphqlOperation(deleteLocation, { input: { id: record.id } })
        );
        results.deleted++;
        
        if (onProgress && results.deleted % 10 === 0) {
          onProgress({ phase: 'deleting', deleted: results.deleted, total: results.totalFound });
        }
      } catch (error) {
        results.errors.push({ id: record.id, error: error.message });
      }
    }
    
    console.log(`✅ Deleted ${results.deleted}/${results.totalFound} Location records`);
    return results;
    
  } catch (error) {
    console.error('❌ Cleanup failed:', error);
    throw error;
  }
}

/**
 * Delete all LocationLaborTargets records for a client
 */
export async function cleanupLocationLaborTargets(clientId, options = {}) {
  const { dryRun = false, onProgress } = options;
  
  console.log(`🧹 Cleaning LocationLaborTargets records for client: ${clientId}`);
  console.log(`Mode: ${dryRun ? 'DRY RUN' : 'LIVE'}`);
  
  const results = { totalFound: 0, deleted: 0, errors: [] };
  
  try {
    let nextToken = null;
    let allRecords = [];
    
    // Fetch all records
    do {
      const result = await API.graphql(
        graphqlOperation(listLocationLaborTargets, {
          filter: { clientId: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      
      const items = result.data.listLocationLaborTargets?.items || [];
      allRecords = allRecords.concat(items);
      nextToken = result.data.listLocationLaborTargets?.nextToken;
      
      if (onProgress) {
        onProgress({ phase: 'fetching', found: allRecords.length });
      }
    } while (nextToken);
    
    results.totalFound = allRecords.length;
    console.log(`Found ${results.totalFound} LocationLaborTargets records`);
    
    if (dryRun) {
      console.log(`DRY RUN - Would delete ${results.totalFound} records`);
      return results;
    }
    
    // Delete records
    for (const record of allRecords) {
      try {
        await API.graphql(
          graphqlOperation(deleteLocationLaborTargets, { input: { id: record.id } })
        );
        results.deleted++;
        
        if (onProgress && results.deleted % 10 === 0) {
          onProgress({ phase: 'deleting', deleted: results.deleted, total: results.totalFound });
        }
      } catch (error) {
        results.errors.push({ id: record.id, error: error.message });
      }
    }
    
    console.log(`✅ Deleted ${results.deleted}/${results.totalFound} LocationLaborTargets records`);
    return results;
    
  } catch (error) {
    console.error('❌ Cleanup failed:', error);
    throw error;
  }
}

/**
 * DEPRECATED: ManagerAssignmentHistory has been removed from the schema
 * Date: January 2025
 * Reason: Implementing new Employee-based hierarchy model
 * Delete all ManagerAssignmentHistory records for a client
 */
// COMMENTED OUT: Function disabled - ManagerAssignmentHistory removed from schema
/*export async function cleanupManagerAssignmentHistory(clientId, options = {}) {
  const { dryRun = false, onProgress } = options;
  
  console.log(`🧹 Cleaning ManagerAssignmentHistory records for client: ${clientId}`);
  console.log(`Mode: ${dryRun ? 'DRY RUN' : 'LIVE'}`);
  
  const results = { totalFound: 0, deleted: 0, errors: [] };
  
  try {
    let nextToken = null;
    let allRecords = [];
    
    // Fetch all records
    do {
      const result = await API.graphql(
        graphqlOperation(managerAssignmentsByClient, {
          clientId,
          limit: 100,
          nextToken
        })
      );
      
      const items = result.data.managerAssignmentsByClient?.items || [];
      allRecords = allRecords.concat(items);
      nextToken = result.data.managerAssignmentsByClient?.nextToken;
      
      if (onProgress) {
        onProgress({ phase: 'fetching', found: allRecords.length });
      }
    } while (nextToken);
    
    results.totalFound = allRecords.length;
    console.log(`Found ${results.totalFound} ManagerAssignmentHistory records`);
    
    if (dryRun) {
      console.log(`DRY RUN - Would delete ${results.totalFound} records`);
      return results;
    }
    
    // Delete records
    for (const record of allRecords) {
      try {
        await API.graphql(
          graphqlOperation(deleteManagerAssignmentHistory, { input: { id: record.id } })
        );
        results.deleted++;
        
        if (onProgress && results.deleted % 10 === 0) {
          onProgress({ phase: 'deleting', deleted: results.deleted, total: results.totalFound });
        }
      } catch (error) {
        results.errors.push({ id: record.id, error: error.message });
      }
    }
    
    console.log(`✅ Deleted ${results.deleted}/${results.totalFound} ManagerAssignmentHistory records`);
    return results;
    
  } catch (error) {
    console.error('❌ Cleanup failed:', error);
    throw error;
  }
}
*/

/**
 * Delete all LocationAssignmentHistory records for a client
 * @deprecated LocationAssignmentHistory table has been removed from schema
 */
export async function cleanupLocationAssignmentHistory(clientId, options = {}) {
  console.log('⚠️ LocationAssignmentHistory table has been removed from schema - skipping');
  return { totalFound: 0, deleted: 0, errors: [] };
}

// Old implementation removed - table no longer exists
// The LocationAssignmentHistory table has been removed from the schema
// History is now tracked in the HierarchyChangeLog table

/**
 * Delete all OrgUnitHistory records for a client
 * @deprecated OrgUnitHistory table has been removed from schema
 */
export async function cleanupOrgUnitHistory(clientId, options = {}) {
  console.log('⚠️ OrgUnitHistory table has been removed from schema - skipping');
  return { totalFound: 0, deleted: 0, errors: [] };
}

// Old implementation removed - table no longer exists
// The OrgUnitHistory table has been removed from the schema
// History is now tracked in the HierarchyChangeLog table

/**
 * Delete all HierarchyChangeLog records for a client
 */
export async function cleanupHierarchyChangeLog(clientId, options = {}) {
  const { dryRun = false, onProgress } = options;
  
  console.log(`🧹 Cleaning HierarchyChangeLog records for client: ${clientId}`);
  console.log(`Mode: ${dryRun ? 'DRY RUN' : 'LIVE'}`);
  
  const results = { totalFound: 0, deleted: 0, errors: [] };
  
  try {
    let nextToken = null;
    let allRecords = [];
    
    // Fetch all records
    do {
      const result = await API.graphql(
        graphqlOperation(hierarchyChangesByClient, {
          clientId,
          limit: 100,
          nextToken
        })
      );
      
      const items = result.data.hierarchyChangesByClient?.items || [];
      allRecords = allRecords.concat(items);
      nextToken = result.data.hierarchyChangesByClient?.nextToken;
      
      if (onProgress) {
        onProgress({ phase: 'fetching', found: allRecords.length });
      }
    } while (nextToken);
    
    results.totalFound = allRecords.length;
    console.log(`Found ${results.totalFound} HierarchyChangeLog records`);
    
    if (dryRun) {
      console.log(`DRY RUN - Would delete ${results.totalFound} records`);
      return results;
    }
    
    // Delete records
    for (const record of allRecords) {
      try {
        await API.graphql(
          graphqlOperation(deleteHierarchyChangeLog, { input: { id: record.id } })
        );
        results.deleted++;
        
        if (onProgress && results.deleted % 10 === 0) {
          onProgress({ phase: 'deleting', deleted: results.deleted, total: results.totalFound });
        }
      } catch (error) {
        results.errors.push({ id: record.id, error: error.message });
      }
    }
    
    console.log(`✅ Deleted ${results.deleted}/${results.totalFound} HierarchyChangeLog records`);
    return results;
    
  } catch (error) {
    console.error('❌ Cleanup failed:', error);
    throw error;
  }
}

/**
 * Delete all OrgUnit records for a client
 */
export async function cleanupOrgUnits(clientId, options = {}) {
  const { dryRun = false, onProgress } = options;
  
  console.log(`🧹 Cleaning OrgUnit records for client: ${clientId}`);
  console.log(`Mode: ${dryRun ? 'DRY RUN' : 'LIVE'}`);
  
  const results = { totalFound: 0, deleted: 0, errors: [] };
  
  try {
    let nextToken = null;
    let allRecords = [];
    
    // Fetch all records
    do {
      const result = await API.graphql(
        graphqlOperation(listOrgUnits, {
          filter: { clientId: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      
      const items = result.data.listOrgUnits?.items || [];
      allRecords = allRecords.concat(items);
      nextToken = result.data.listOrgUnits?.nextToken;
      
      if (onProgress) {
        onProgress({ phase: 'fetching', found: allRecords.length });
      }
    } while (nextToken);
    
    results.totalFound = allRecords.length;
    console.log(`Found ${results.totalFound} OrgUnit records`);
    
    if (dryRun) {
      console.log(`DRY RUN - Would delete ${results.totalFound} records`);
      return results;
    }
    
    // Delete records
    for (const record of allRecords) {
      try {
        await API.graphql(
          graphqlOperation(deleteOrgUnit, { input: { id: record.id } })
        );
        results.deleted++;
        
        if (onProgress && results.deleted % 10 === 0) {
          onProgress({ phase: 'deleting', deleted: results.deleted, total: results.totalFound });
        }
      } catch (error) {
        results.errors.push({ id: record.id, error: error.message });
      }
    }
    
    console.log(`✅ Deleted ${results.deleted}/${results.totalFound} OrgUnit records`);
    return results;
    
  } catch (error) {
    console.error('❌ Cleanup failed:', error);
    throw error;
  }
}

/**
 * Delete all Employee records for a client and clear User.employeeId references
 */
export async function cleanupEmployees(clientId, options = {}) {
  const { dryRun = false, onProgress } = options;
  
  console.log(`🧹 Cleaning Employee records for client: ${clientId}`);
  console.log(`Mode: ${dryRun ? 'DRY RUN' : 'LIVE'}`);
  
  const results = { 
    totalFound: 0, 
    deleted: 0, 
    usersUpdated: 0,
    errors: [] 
  };
  
  try {
    let nextToken = null;
    let allEmployees = [];
    
    // Fetch all Employee records
    do {
      const result = await API.graphql(
        graphqlOperation(listEmployees, {
          filter: { clientId: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      
      const items = result.data.listEmployees?.items || [];
      allEmployees = allEmployees.concat(items);
      nextToken = result.data.listEmployees?.nextToken;
      
      if (onProgress) {
        onProgress({ phase: 'fetching', found: allEmployees.length });
      }
    } while (nextToken);
    
    results.totalFound = allEmployees.length;
    console.log(`Found ${results.totalFound} Employee records`);
    
    // Also fetch all Users to clear their employeeId fields
    console.log(`Fetching Users to clear employeeId references...`);
    let allUsers = [];
    nextToken = null;
    
    do {
      const result = await API.graphql(
        graphqlOperation(listUsers, {
          filter: { 
            client: { eq: clientId },
            employeeId: { attributeExists: true }
          },
          limit: 100,
          nextToken
        })
      );
      
      const items = result.data.listUsers?.items || [];
      allUsers = allUsers.concat(items);
      nextToken = result.data.listUsers?.nextToken;
    } while (nextToken);
    
    console.log(`Found ${allUsers.length} Users with employeeId to clear`);
    
    if (dryRun) {
      console.log(`DRY RUN - Would delete ${results.totalFound} Employee records`);
      console.log(`DRY RUN - Would clear employeeId from ${allUsers.length} User records`);
      return results;
    }
    
    // First, clear employeeId from all Users
    console.log(`Clearing employeeId from ${allUsers.length} User records...`);
    for (const user of allUsers) {
      try {
        await API.graphql(
          graphqlOperation(updateUser, {
            input: {
              id: user.id,
              employeeId: null  // Clear the employeeId field
            }
          })
        );
        results.usersUpdated++;
        
        if (onProgress && results.usersUpdated % 10 === 0) {
          onProgress({ 
            phase: 'clearing-users', 
            updated: results.usersUpdated, 
            total: allUsers.length 
          });
        }
      } catch (error) {
        console.error(`Failed to clear employeeId for user ${user.userName}:`, error);
        results.errors.push({ 
          userId: user.id, 
          userName: user.userName,
          error: `Clear employeeId: ${error.message}` 
        });
      }
    }
    
    console.log(`✅ Cleared employeeId from ${results.usersUpdated}/${allUsers.length} User records`);
    
    // Then delete Employee records
    console.log(`Deleting ${allEmployees.length} Employee records...`);
    for (const record of allEmployees) {
      try {
        await API.graphql(
          graphqlOperation(deleteEmployee, { input: { id: record.id } })
        );
        results.deleted++;
        
        if (onProgress && results.deleted % 10 === 0) {
          onProgress({ 
            phase: 'deleting-employees', 
            deleted: results.deleted, 
            total: results.totalFound 
          });
        }
      } catch (error) {
        results.errors.push({ 
          id: record.id, 
          error: `Delete employee: ${error.message}` 
        });
      }
    }
    
    console.log(`✅ Deleted ${results.deleted}/${results.totalFound} Employee records`);
    console.log(`✅ Updated ${results.usersUpdated} User records`);
    return results;
    
  } catch (error) {
    console.error('❌ Cleanup failed:', error);
    throw error;
  }
}

/**
 * Delete all RoleDefinition records for a client
 */
export async function cleanupRoles(clientId, options = {}) {
  const { dryRun = false, onProgress } = options;
  
  console.log(`🧹 Cleaning RoleDefinition records for client: ${clientId}`);
  console.log(`Mode: ${dryRun ? 'DRY RUN' : 'LIVE'}`);
  
  const results = { totalFound: 0, deleted: 0, errors: [] };
  
  try {
    let nextToken = null;
    let allRecords = [];
    
    // Fetch all records
    do {
      const result = await API.graphql(
        graphqlOperation(listRoleDefinitions, {
          filter: { clientId: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      
      const items = result.data.listRoleDefinitions?.items || [];
      allRecords = allRecords.concat(items);
      nextToken = result.data.listRoleDefinitions?.nextToken;
      
      if (onProgress) {
        onProgress({ phase: 'fetching', found: allRecords.length });
      }
    } while (nextToken);
    
    results.totalFound = allRecords.length;
    console.log(`Found ${results.totalFound} RoleDefinition records`);
    
    if (dryRun) {
      console.log(`DRY RUN - Would delete ${results.totalFound} records`);
      return results;
    }
    
    // Delete records
    for (const record of allRecords) {
      try {
        await API.graphql(
          graphqlOperation(deleteRoleDefinition, { input: { id: record.id } })
        );
        results.deleted++;
        
        if (onProgress && results.deleted % 10 === 0) {
          onProgress({ phase: 'deleting', deleted: results.deleted, total: results.totalFound });
        }
      } catch (error) {
        results.errors.push({ id: record.id, error: error.message });
      }
    }
    
    console.log(`✅ Deleted ${results.deleted}/${results.totalFound} RoleDefinition records`);
    return results;
    
  } catch (error) {
    console.error('❌ Cleanup failed:', error);
    throw error;
  }
}

/**
 * Delete all LocationEmployee records for a client
 */
export async function cleanupLocationEmployees(clientId, options = {}) {
  const { dryRun = false, onProgress } = options;
  
  console.log(`🧹 Cleaning LocationEmployee records for client: ${clientId}`);
  console.log(`Mode: ${dryRun ? 'DRY RUN' : 'LIVE'}`);
  
  const results = { totalFound: 0, deleted: 0, errors: [] };
  
  try {
    let nextToken = null;
    let allRecords = [];
    
    // Fetch all records
    do {
      const result = await API.graphql(
        graphqlOperation(listLocationEmployees, {
          filter: { clientId: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      
      const items = result.data.listLocationEmployees?.items || [];
      allRecords = allRecords.concat(items);
      nextToken = result.data.listLocationEmployees?.nextToken;
      
      if (onProgress) {
        onProgress({ phase: 'fetching', found: allRecords.length });
      }
    } while (nextToken);
    
    results.totalFound = allRecords.length;
    console.log(`Found ${results.totalFound} LocationEmployee records`);
    
    if (dryRun) {
      console.log(`DRY RUN - Would delete ${results.totalFound} records`);
      return results;
    }
    
    // Delete records
    for (const record of allRecords) {
      try {
        await API.graphql(
          graphqlOperation(deleteLocationEmployee, { input: { id: record.id } })
        );
        results.deleted++;
        
        if (onProgress && results.deleted % 10 === 0) {
          onProgress({ phase: 'deleting', deleted: results.deleted, total: results.totalFound });
        }
      } catch (error) {
        results.errors.push({ id: record.id, error: error.message });
      }
    }
    
    console.log(`✅ Deleted ${results.deleted}/${results.totalFound} LocationEmployee records`);
    return results;
    
  } catch (error) {
    console.error('❌ Cleanup failed:', error);
    throw error;
  }
}

/**
 * Delete all OrgUnitEmployee records for a client
 */
export async function cleanupOrgUnitEmployees(clientId, options = {}) {
  const { dryRun = false, onProgress } = options;
  
  console.log(`🧹 Cleaning OrgUnitEmployee records for client: ${clientId}`);
  console.log(`Mode: ${dryRun ? 'DRY RUN' : 'LIVE'}`);
  
  const results = { totalFound: 0, deleted: 0, errors: [] };
  
  try {
    let nextToken = null;
    let allRecords = [];
    
    // Fetch all records
    do {
      const result = await API.graphql(
        graphqlOperation(listOrgUnitEmployees, {
          filter: { clientId: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      
      const items = result.data.listOrgUnitEmployees?.items || [];
      allRecords = allRecords.concat(items);
      nextToken = result.data.listOrgUnitEmployees?.nextToken;
      
      if (onProgress) {
        onProgress({ phase: 'fetching', found: allRecords.length });
      }
    } while (nextToken);
    
    results.totalFound = allRecords.length;
    console.log(`Found ${results.totalFound} OrgUnitEmployee records`);
    
    if (dryRun) {
      console.log(`DRY RUN - Would delete ${results.totalFound} records`);
      return results;
    }
    
    // Delete records
    for (const record of allRecords) {
      try {
        await API.graphql(
          graphqlOperation(deleteOrgUnitEmployee, { input: { id: record.id } })
        );
        results.deleted++;
        
        if (onProgress && results.deleted % 10 === 0) {
          onProgress({ phase: 'deleting', deleted: results.deleted, total: results.totalFound });
        }
      } catch (error) {
        results.errors.push({ id: record.id, error: error.message });
      }
    }
    
    console.log(`✅ Deleted ${results.deleted}/${results.totalFound} OrgUnitEmployee records`);
    return results;
    
  } catch (error) {
    console.error('❌ Cleanup failed:', error);
    throw error;
  }
}

/**
 * Delete ALL records for all tables
 */
export async function cleanupAllTables(clientId, options = {}) {
  const { dryRun = false, onProgress } = options;
  
  console.log(`🧹🧹🧹 CLEANING ALL TABLES for client: ${clientId}`);
  console.log(`Mode: ${dryRun ? 'DRY RUN' : 'LIVE - THIS WILL DELETE EVERYTHING!'}`);
  
  const results = {
    locations: null,
    laborTargets: null,
    locationEmployees: null,
    orgUnitEmployees: null,
    employees: null,
    roles: null,
    hierarchyChangeLog: null,
    orgUnits: null
  };
  
  try {
    // Order matters - delete dependent tables first
    
    // 1. Delete history/audit tables first
    console.log('\n📋 Step 1: Cleaning HierarchyChangeLog...');
    results.hierarchyChangeLog = await cleanupHierarchyChangeLog(clientId, { dryRun, onProgress });
    
    // 2. Delete employee assignment tables (must be before Employee table)
    console.log('\n📋 Step 2: Cleaning LocationEmployee assignments...');
    results.locationEmployees = await cleanupLocationEmployees(clientId, { dryRun, onProgress });
    
    console.log('\n📋 Step 3: Cleaning OrgUnitEmployee assignments...');
    results.orgUnitEmployees = await cleanupOrgUnitEmployees(clientId, { dryRun, onProgress });
    
    // 3. Delete labor targets
    console.log('\n📋 Step 4: Cleaning LocationLaborTargets...');
    results.laborTargets = await cleanupLocationLaborTargets(clientId, { dryRun, onProgress });
    
    // 4. Delete main entity tables
    console.log('\n📋 Step 5: Cleaning Locations...');
    results.locations = await cleanupLocations(clientId, { dryRun, onProgress });
    
    console.log('\n📋 Step 6: Cleaning OrgUnits...');
    results.orgUnits = await cleanupOrgUnits(clientId, { dryRun, onProgress });
    
    // 5. Delete Employee table (after all assignments are removed)
    console.log('\n📋 Step 7: Cleaning Employee records...');
    results.employees = await cleanupEmployees(clientId, { dryRun, onProgress });
    
    // 6. Delete Role definitions last
    console.log('\n📋 Step 8: Cleaning Role definitions...');
    results.roles = await cleanupRoles(clientId, { dryRun, onProgress });
    
    console.log('\n✅ All tables cleaned successfully!');
    console.log('\n📊 Summary:');
    console.log(`  - Locations: ${results.locations?.deleted || 0} deleted`);
    console.log(`  - LocationLaborTargets: ${results.laborTargets?.deleted || 0} deleted`);
    console.log(`  - LocationEmployees: ${results.locationEmployees?.deleted || 0} deleted`);
    console.log(`  - OrgUnits: ${results.orgUnits?.deleted || 0} deleted`);
    console.log(`  - OrgUnitEmployees: ${results.orgUnitEmployees?.deleted || 0} deleted`);
    console.log(`  - Employees: ${results.employees?.deleted || 0} deleted`);
    console.log(`  - Roles: ${results.roles?.deleted || 0} deleted`);
    console.log(`  - HierarchyChangeLog: ${results.hierarchyChangeLog?.deleted || 0} deleted`);
    
    return results;
    
  } catch (error) {
    console.error('❌ Cleanup all tables failed:', error);
    throw error;
  }
}